import React, { useId, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Money from "components/money";
import Collapsible from "react-collapsible";
import { ReactComponent as ArrowDown } from "assets/svgs/arrow.svg";
import Ticket from "assets/images/ticket.png";
import { useGlobalContext } from "context/global";
import { findClosestBox } from "utils/cart";
import SaveChangesButton from "./save-changes";
import DiscountForm from "components/subscription-discounts/discount-form";
import DiscountCodes from "components/subscription-discounts/discount-codes";
import clsx from "clsx/lite";
import useReactivationFlow from "hooks/useReactivationFlow";
import { TOGGLE_SUBS_CART } from "utils/action-types";
import ConfirmMealsButton from "components/reactivation-funnel/confirm-meals-button";

const SubscriptionCartFooter = ({ isSticky }) => {
	const {
		totalLineItemPrice, totalLineItemDiscountedPrice, deliveryPrice, draftChanges, activeBox, totalWeight
	} = useSelector((state) => state.account.subscription);
	const { translation, cartSettings } = useGlobalContext();
	const dispatch = useDispatch();
	const minBox = findClosestBox(cartSettings.boxes, totalWeight);

	const { reactivationFlow } = useReactivationFlow();
	const isReactivationFlowActive = reactivationFlow.step === 2;

	const savings = parseFloat(totalLineItemPrice - totalLineItemDiscountedPrice).toFixed(2);

	const triggerId = useId("trigger");
	const arrowId = useId("arrow");
	const [isOpen, setOpen] = useState(true);
	const [isPromoOpen, setPromoOpen] = useState(false);

	// If the reactivation flow is active, show the save changes button with a custom callback and text
	if (isReactivationFlowActive) {
		return (
			<div className="flex flex-col text-center border-t p-4">
				<ConfirmMealsButton callback={() => dispatch({ type: TOGGLE_SUBS_CART })} />
			</div>
		);
	}

	if (draftChanges.unsaved || (minBox.size.value !== activeBox.size.value)) {
		return (
			<div className="flex flex-col text-center border-t p-4">
				<span className="text-sm pb-2 text-green-discount">
					{translation.subscription?.price_calculation_message}
				</span>
				<SaveChangesButton />
			</div>
		);
	}

	return (
		<>
			<div className="px-4 summaryCollapsible">
				<Collapsible
					transitionTime="200"
					open={!isSticky ? isOpen : false}
					onOpening={() => setOpen(true)}
					onClosing={() => setOpen(false)}
					className={clsx("border-t pt-4", isSticky && "!px-0")}
					openedClassName={clsx("!border-none", isSticky && "!px-0")}
					trigger={[
						<div className="mb-3 flex flex-col w-full pr-4" key={triggerId}>
							<div className="flex justify-between items-center">
								<span className="font-bold text-sm">{translation.subscription?.order_total}</span>
								<div className="flex flex-wrap justify-end gap-2">
									<span className="font-bold"><Money value={totalLineItemDiscountedPrice} /></span>
								</div>
							</div>
							<span className="text-smaller opacity-40"> {translation?.other?.incVatShipping}</span>
							<span className="text-smaller opacity-40"> {translation?.cart?.recurringNextStep}</span>
						</div>,
						<ArrowDown key={arrowId}
							className={clsx(
								"uppercase inline float-right transition-all duration-600 mt-2 self-start",
								!isOpen && "rotate-180",
							)} />
					]}
				>
					{/* Total price */}
					{totalLineItemDiscountedPrice > 0 &&
						<div className="flex py-2 pt-2 justify-between items-center border-t">
							<span className="text-sm">{translation.cart?.box_price}</span>
							<div className="text-sm">
								<Money value={totalLineItemPrice} />
							</div>
						</div>}
					{savings > 0 &&
						<div className="flex justify-between items-center py-2">
							<span className="text-sm">{translation.subscription?.order_discount}</span>
							<span className="text-sm"> -<Money value={savings} /> </span>
						</div>
					}

					<div className="flex justify-between items-center py-2">
						<span className="text-sm capitalize">{translation?.subscription?.shipping}</span>
						<span className="text-sm">
							{deliveryPrice > 0 ? <Money value={deliveryPrice} /> : translation.shopping_flow?.free ?? "Free"}
						</span>
					</div>
					{/* Discount Form Collapsible */}
					<div className="discountCollapsible">
						<Collapsible
							open={isPromoOpen}
							transitionTime={200}
							trigger={
								<div className="w-full flex justify-between items-center cursor-pointer select-none pb-4">
									<span className="text-sm">{translation.other.promo_code || "Promo code"}</span>
									<ArrowDown className={`transition-transform duration-300 ${!isPromoOpen ? "rotate-180" : ""}`} />
								</div>
							}
							onOpening={() => setPromoOpen(true)}
							onClosing={() => setPromoOpen(false)}
						>
							<div className="pb-4 border-b">
								<DiscountForm
									className={{
										form: "flex gap-2 h-10",
										input: "border rounded p-1 text-sm size-full",
										button: "bg-platinum rounded-3xl w-1/2 h-full px-1 py-2 text-sm"
									}}
								/>
								<DiscountCodes />
								<div className="flex justify-between items-center mt-4">
									<span className="text-sm flex items-center space-x-2 gap-2">
										<img className="w-4 mb-0" src={Ticket} alt="Ticket" />
										{`${translation.other.discounts}`}
									</span>
									<span className="text-sm">-<Money value={savings} /></span>
								</div>
							</div>
						</Collapsible>
					</div>

				</Collapsible>
			</div>

		</>
	);
};

export default SubscriptionCartFooter;